import { supabase } from './supabase';
export type CheckoutMode = 'test' | 'live';
async function request(path: string, body: unknown, mode: CheckoutMode = 'test') {
  const {data}=await supabase.auth.getSession();
  if(!data.session) throw new Error('Please sign in to use checkout.');
  const response=await fetch(`/api/paystack/${mode}/${path}`, {
    method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${data.session.access_token}`},
    body:JSON.stringify(body),signal:AbortSignal.timeout(45000),
  });
  const result=await response.json().catch(()=>null);
  if(!response.ok) throw new Error(result?.error || 'Checkout is unavailable. Please retry.');
  return result;
}
export async function startTestCheckout() {
  const result=await request('checkout',{});
  const url=new URL(result.authorizationUrl);
  if(result.mode!=='test' || url.protocol!=='https:' || url.hostname!=='checkout.paystack.com') throw new Error('Invalid test checkout response.');
  window.location.assign(url.href);
}
export async function verifyTestCheckout(reference: string) {
  const result=await request('verify',{reference});
  if(result.mode!=='test' || result.paymentStatus!=='success') throw new Error('Test payment has not been confirmed.');
  window.dispatchEvent(new Event('lavyn:generation-usage-changed'));
  return result as {premiumActivated:boolean;paymentStatus:'success'};
}
export async function checkoutAvailability(): Promise<CheckoutMode> {
  const response = await fetch('/api/paystack/status', { cache: 'no-store', signal: AbortSignal.timeout(10000) });
  const result = await response.json();
  if (!response.ok || !['test', 'live'].includes(result?.checkoutMode) ||
      (result.checkoutMode === 'live' && result.liveCheckoutEnabled !== true)) {
    throw new Error('Checkout is currently unavailable.');
  }
  return result.checkoutMode;
}
export async function startLiveCheckout(): Promise<{paymentStatus:'pending';reference:string}|void> {
  const result = await request('checkout', {}, 'live');
  if (result.mode === 'live' && result.paymentStatus === 'pending' && result.authorizationUrl == null &&
      typeof result.reference === 'string') {
    return {paymentStatus:'pending', reference:result.reference};
  }
  const url = new URL(result.authorizationUrl);
  if (result.mode !== 'live' || url.protocol !== 'https:' || url.hostname !== 'checkout.paystack.com' ||
      url.port || url.username || url.password) throw new Error('Invalid checkout response.');
  window.location.assign(url.href);
}
export async function verifyLiveCheckout(reference: string) {
  const result = await request('verify', { reference }, 'live');
  if (result.mode !== 'live' || !['success','pending','failed','review'].includes(result.paymentStatus)) {
    throw new Error('Payment status could not be confirmed.');
  }
  if (result.premiumActivated === true) window.dispatchEvent(new Event('lavyn:generation-usage-changed'));
  return result as {
    premiumActivated: boolean;
    paymentStatus: 'success'|'pending'|'failed'|'review';
    reviewReason?: string;
  };
}

export async function reconcileLiveAccount() {
  const result = await request('reconcile', {}, 'live');
  if (result.mode !== 'live' ||
      !['none','success','pending','failed','review'].includes(result.paymentStatus) ||
      typeof result.hasActivePremium !== 'boolean' ||
      ![null,'live','test','manual','other'].includes(result.premiumSource)) {
    throw new Error('The existing payment could not be safely reconciled.');
  }
  if (result.premiumActivated === true || result.hasActivePremium) {
    window.dispatchEvent(new Event('lavyn:generation-usage-changed'));
  }
  return result as {
    mode: 'live';
    paymentStatus: 'none'|'success'|'pending'|'failed'|'review';
    premiumActivated: boolean;
    hasActivePremium: boolean;
    premiumSource: 'live'|'test'|'manual'|'other'|null;
  };
}
